// src/pages/contracts/ContractsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Upload,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Send,
  Download,
  Eye,
  Users,
  Calendar,
  Search,
  Filter,
  Pen,
  FolderOpen,
  Trash2,
  Copy
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { contractService } from '../../services/contract.service';
import { userService } from '../../services/user.service';
import { Contract, ContractStatus, CreateContractDto, ContractTemplate, ContractTemplateType } from '../../types/contract.types';
import { UserProfile } from '../../types/user.types';
import { Project } from '../../types/project.types';
import { SignatureCanvas } from '../../components/contracts/SignatureCanvas';
import { generateMergedComplianceDocument } from '../../utils/signingUtils';
import { contractGenerationService } from '../../services/contractGeneration.service';
import toast from 'react-hot-toast';
import { formatSafeDate } from '../../utils/dateUtils';

const ContractsPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { projects } = useProject();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSendFromTemplateModal, setShowSendFromTemplateModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplate | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'contracts' | 'templates'>('contracts');
  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState<string | null>(null);
  const [generatingPackage, setGeneratingPackage] = useState<string | null>(null);
  const [showGenerateAgreementModal, setShowGenerateAgreementModal] = useState(false);
  const [generateProjectId, setGenerateProjectId] = useState('');
  const [generatingAgreement, setGeneratingAgreement] = useState(false);
  const [sendingToClient, setSendingToClient] = useState<string | null>(null);

  // Handle downloading the complete compliance package (original doc + e-sign certificate merged)
  const handleDownloadPackage = async (contract: Contract) => {
    try {
      setGeneratingPackage(contract.id);

      const blob = await generateMergedComplianceDocument(contract as any);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${contract.title.replace(/[^a-zA-Z0-9]/g, '_')}_Compliance_Package.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Compliance package downloaded');
    } catch (error) {
      console.error('Error generating compliance package:', error);
      toast.error('Failed to generate compliance package');
    } finally {
      setGeneratingPackage(null);
    }
  };

  // super_admin, provider_admin, provider_user, and client_admin can create contracts
  const canCreateContracts = userProfile?.role === 'super_admin' ||
                             userProfile?.role === 'provider_admin' ||
                             userProfile?.role === 'provider_user' ||
                             userProfile?.role === 'client_admin';
  const isAdmin = userProfile?.role === 'super_admin' || userProfile?.role === 'provider_admin' || userProfile?.role === 'client_admin';

  useEffect(() => {
    loadContracts();
  }, [currentUser, userProfile]);

  const loadContracts = async () => {
    if (!currentUser || !userProfile) return;

    try {
      let contractList: Contract[];

      // Provider_admin sees ALL contracts across all organizations
      if (userProfile.role === 'provider_admin') {
        contractList = await contractService.getAllContracts();
        const templateList = await contractService.getAllTemplates();
        setTemplates(templateList);
      }
      // Other admins and provider_user see their org's contracts
      else if ((isAdmin || userProfile.role === 'provider_user') && userProfile.organizationId) {
        contractList = await contractService.getOrganizationContracts(userProfile.organizationId);
        const templateList = await contractService.getTemplates(userProfile.organizationId);
        setTemplates(templateList);
      }
      // Regular users see contracts where they are a signer
      else {
        contractList = await contractService.getUserContracts(currentUser.uid);
      }
      setContracts(contractList);
    } catch (error) {
      console.error('Error loading contracts:', error);
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: ContractStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-accent-500" />;
      case 'pending_signatures':
      case 'partially_signed':
        return <Clock className="h-5 w-5 text-secondary-500" />;
      case 'expired':
      case 'cancelled':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: ContractStatus) => {
    const styles: Record<ContractStatus, string> = {
      draft: 'bg-gray-100 text-gray-700',
      pending_signatures: 'bg-secondary-100 text-secondary-700',
      partially_signed: 'bg-primary-100 text-primary-700',
      completed: 'bg-accent-100 text-accent-700',
      expired: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-600'
    };

    const labels: Record<ContractStatus, string> = {
      draft: 'Draft',
      pending_signatures: 'Pending Signatures',
      partially_signed: 'Partially Signed',
      completed: 'Completed',
      expired: 'Expired',
      cancelled: 'Cancelled'
    };

    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const filteredContracts = contracts.filter(contract => {
    if (filter === 'pending') {
      return ['pending_signatures', 'partially_signed', 'draft'].includes(contract.status);
    }
    if (filter === 'completed') {
      return contract.status === 'completed';
    }
    if (searchTerm) {
      return contract.title.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  const handleViewContract = (contract: Contract) => {
    setSelectedContract(contract);
    setShowDetailModal(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await contractService.deleteTemplate(templateId);
      toast.success('Template deleted');
      loadContracts();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleSendFromTemplate = (template: ContractTemplate) => {
    setSelectedTemplate(template);
    setShowSendFromTemplateModal(true);
  };

  const getTemplateTypeLabel = (type: ContractTemplateType) => {
    switch (type) {
      case 'w9': return 'W-9 Tax Form';
      case 'direct_deposit': return 'Direct Deposit';
      case 'employment': return 'Employment';
      case 'nda': return 'NDA';
      case 'service_agreement': return 'Service Agreement';
      default: return 'Other';
    }
  };

  // Handle generating a service agreement for an existing project
  const handleGenerateAgreement = async () => {
    if (!generateProjectId) return;
    setGeneratingAgreement(true);
    try {
      await contractGenerationService.generateServiceAgreementPDF(generateProjectId);
      toast.success('Service agreement draft created!');
      setShowGenerateAgreementModal(false);
      setGenerateProjectId('');
      loadContracts();
    } catch (error: any) {
      console.error('Error generating service agreement:', error);
      toast.error(error.message || 'Failed to generate service agreement');
    } finally {
      setGeneratingAgreement(false);
    }
  };

  // Handle sending a draft contract to client for signing
  const handleSendToClient = async (contractId: string) => {
    if (!window.confirm('This will email the client admin and require their signature. Continue?')) return;
    setSendingToClient(contractId);
    try {
      await contractGenerationService.sendContractToClient(contractId);
      toast.success('Contract sent to client for signing!');
      loadContracts();
    } catch (error: any) {
      console.error('Error sending to client:', error);
      toast.error(error.message || 'Failed to send contract');
    } finally {
      setSendingToClient(null);
    }
  };

  // Get projects that don't have a service agreement yet (for the generate modal)
  const projectsWithoutAgreement = projects.filter(
    (p: Project) => !p.serviceAgreementId && p.organizationId
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Contracts</h1>
          <p className="text-gray-500 mt-1">Manage contracts, signatures, and templates</p>
        </div>
        {canCreateContracts && (
          <div className="flex gap-2">
            {activeTab === 'templates' ? (
              <button
                onClick={() => setShowTemplateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200"
              >
                <Plus className="h-5 w-5" />
                New Template
              </button>
            ) : (
              <div className="flex gap-2">
                {userProfile?.role === 'provider_admin' && (
                  <button
                    onClick={() => setShowGenerateAgreementModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-200"
                  >
                    <FileText className="h-5 w-5" />
                    Generate Service Agreement
                  </button>
                )}
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200"
                >
                  <Plus className="h-5 w-5" />
                  New Contract
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      {canCreateContracts && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setActiveTab('contracts')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                activeTab === 'contracts'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="h-4 w-4" />
              Contracts ({contracts.length})
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                activeTab === 'templates'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FolderOpen className="h-4 w-4" />
              Templates ({templates.length})
            </button>
          </div>
        </div>
      )}

      {activeTab === 'contracts' && (
        <>
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search contracts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'completed'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contracts List */}
      {filteredContracts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">No contracts found</h3>
          <p className="text-gray-500">
            {canCreateContracts ? 'Create your first contract to get started.' : 'You have no pending contracts.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredContracts.map((contract) => {
            const userNeedsToSign = contract.requiredSigners.find(
              s => s.userId === currentUser?.uid && !s.signed
            );

            return (
              <div
                key={contract.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary-50 rounded-xl">
                    {getStatusIcon(contract.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-gray-800">{contract.title}</h3>
                        {contract.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                            {contract.description}
                          </p>
                        )}
                      </div>
                      {getStatusBadge(contract.status)}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4" />
                        {formatSafeDate(contract.createdAt, 'MMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users className="h-4 w-4" />
                        {contract.requiredSigners.filter(s => s.signed).length}/{contract.requiredSigners.length} signed
                      </span>
                      {contract.projectId && (
                        <span className="flex items-center gap-1.5">
                          <FileText className="h-4 w-4" />
                          {projects.find(p => p.id === contract.projectId)?.name || 'Project'}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={() => handleViewContract(contract)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </button>
                      {userNeedsToSign && (
                        <button
                          onClick={() => handleViewContract(contract)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
                        >
                          <Pen className="h-4 w-4" />
                          Sign Now
                        </button>
                      )}

                      {/* Send to Client button for draft service agreements/amendments */}
                      {(contract.contractType === 'service_agreement' || contract.contractType === 'amendment') &&
                        contract.status === 'draft' &&
                        userProfile?.role === 'provider_admin' && (
                        <button
                          onClick={() => handleSendToClient(contract.id)}
                          disabled={sendingToClient === contract.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                        >
                          <Send className="h-4 w-4" />
                          {sendingToClient === contract.id ? 'Sending...' : 'Send to Client'}
                        </button>
                      )}

                      {/* Download Compliance Package */}
                      <button
                        onClick={() => handleDownloadPackage(contract)}
                        disabled={generatingPackage === contract.id || contract.signatures.length === 0}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition-colors disabled:opacity-50"
                        title={contract.signatures.length === 0 ? 'No signatures yet' : 'Download compliance package (original + e-sign certificate)'}
                      >
                        {generatingPackage === contract.id ? (
                          <>
                            <div className="h-4 w-4 border-2 border-secondary-700 border-t-transparent rounded-full animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4" />
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && canCreateContracts && (
        <div className="space-y-4">
          {templates.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No templates yet</h3>
              <p className="text-gray-500 mb-4">
                Create reusable templates for common contracts like W-9s and Direct Deposit forms.
              </p>
              <button
                onClick={() => setShowTemplateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create Template
              </button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 bg-primary-50 rounded-lg">
                      <FileText className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 truncate">{template.name}</h3>
                      <p className="text-xs text-gray-500">{getTemplateTypeLabel(template.templateType)}</p>
                    </div>
                    {template.mandatory && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                        Mandatory
                      </span>
                    )}
                  </div>

                  {template.description && (
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{template.description}</p>
                  )}

                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleSendFromTemplate(template)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
                    >
                      <Send className="h-4 w-4" />
                      Send
                    </button>
                    <a
                      href={template.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="flex items-center justify-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Contract Modal */}
      {showCreateModal && (
        <CreateContractModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadContracts();
          }}
          projects={projects}
        />
      )}

      {/* Contract Detail Modal */}
      {showDetailModal && selectedContract && (
        <ContractDetailModal
          contract={selectedContract}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedContract(null);
          }}
          onSigned={() => {
            loadContracts();
          }}
        />
      )}

      {/* Create Template Modal */}
      {showTemplateModal && (
        <CreateTemplateModal
          onClose={() => setShowTemplateModal(false)}
          onCreated={() => {
            setShowTemplateModal(false);
            loadContracts();
          }}
        />
      )}

      {/* Send From Template Modal */}
      {showSendFromTemplateModal && selectedTemplate && (
        <SendFromTemplateModal
          template={selectedTemplate}
          projects={projects}
          onClose={() => {
            setShowSendFromTemplateModal(false);
            setSelectedTemplate(null);
          }}
          onSent={() => {
            setShowSendFromTemplateModal(false);
            setSelectedTemplate(null);
            setActiveTab('contracts');
            loadContracts();
          }}
        />
      )}

      {/* Generate Service Agreement Modal */}
      {showGenerateAgreementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Generate Service Agreement</h2>
                <button onClick={() => setShowGenerateAgreementModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Select a project to generate a service agreement draft. The agreement will be pre-filled with the project's billing terms, dates, and goals.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                <select
                  value={generateProjectId}
                  onChange={(e) => setGenerateProjectId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a project...</option>
                  {projectsWithoutAgreement.map((p: Project) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {projectsWithoutAgreement.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">All projects already have service agreements.</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowGenerateAgreementModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateAgreement}
                  disabled={!generateProjectId || generatingAgreement}
                  className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {generatingAgreement ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4" />
                      Generate Draft
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Template Modal Component
interface CreateTemplateModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({ onClose, onCreated }) => {
  const { currentUser, userProfile } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState<ContractTemplateType>('other');
  const [file, setFile] = useState<File | null>(null);
  const [mandatory, setMandatory] = useState(false);
  const [loading, setLoading] = useState(false);

  const isProviderAdmin = userProfile?.role === 'provider_admin';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userProfile || !file) return;

    // For provider_admin without org, use 'provider' as the org ID
    const effectiveOrgId = userProfile.organizationId || (isProviderAdmin ? 'provider' : '');

    if (!effectiveOrgId) {
      toast.error('Your account is not associated with an organization. Please contact an administrator.');
      return;
    }

    setLoading(true);
    try {
      await contractService.createTemplate(
        {
          name,
          description,
          documentFile: file,
          templateType,
          mandatory
        },
        effectiveOrgId,
        currentUser.uid,
        userProfile.name
      );

      toast.success('Template created successfully');
      onCreated();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">New Contract Template</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="W-9 Tax Form"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Optional description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Type</label>
            <select
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value as ContractTemplateType)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="w9">W-9 Tax Form</option>
              <option value="direct_deposit">Direct Deposit Authorization</option>
              <option value="employment">Employment Agreement</option>
              <option value="nda">Non-Disclosure Agreement</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document (PDF)</label>
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center">
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-5 w-5 text-primary-500" />
                  <span className="text-sm text-gray-700">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm text-gray-500">Click to upload PDF</span>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={mandatory}
                onChange={(e) => setMandatory(e.target.checked)}
                className="mt-1 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <div>
                <div className="text-sm font-medium text-amber-800">Default to Mandatory</div>
                <div className="text-xs text-amber-600 mt-0.5">
                  Contracts created from this template will block app access until signed.
                </div>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !file || !name}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Template
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Send From Template Modal Component
interface SendFromTemplateModalProps {
  template: ContractTemplate;
  projects: any[];
  onClose: () => void;
  onSent: () => void;
}

const SendFromTemplateModal: React.FC<SendFromTemplateModalProps> = ({ template, projects, onClose, onSent }) => {
  const { currentUser, userProfile } = useAuth();
  const [projectId, setProjectId] = useState('');
  const [selectedSigners, setSelectedSigners] = useState<{
    userId: string;
    payRate: number;
    payType: 'hourly' | 'per_lead' | 'flat';
    mandatory: boolean;
  }[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const isProviderAdmin = userProfile?.role === 'provider_admin';
  const isProviderUser = userProfile?.role === 'provider_user';

  useEffect(() => {
    loadUsers();
  }, [userProfile]);

  const loadUsers = async () => {
    if (!userProfile) {
      setLoadingUsers(false);
      return;
    }

    try {
      let userList: UserProfile[];

      // Provider_admin can see ALL users across all organizations
      if (isProviderAdmin) {
        userList = await userService.getAllUsers();
      } else if (userProfile.organizationId) {
        userList = await userService.getOrganizationUsers(userProfile.organizationId);
      } else {
        setLoadingUsers(false);
        return;
      }

      if (isProviderUser) {
        userList = userList.filter(u => u.role === 'field');
      }
      setUsers(userList);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUser = (userId: string) => {
    const existing = selectedSigners.find(s => s.userId === userId);
    if (existing) {
      setSelectedSigners(selectedSigners.filter(s => s.userId !== userId));
    } else {
      setSelectedSigners([...selectedSigners, { userId, payRate: 15, payType: 'hourly', mandatory: template.mandatory }]);
    }
  };

  const updateSignerMandatory = (userId: string, mandatory: boolean) => {
    setSelectedSigners(selectedSigners.map(s =>
      s.userId === userId ? { ...s, mandatory } : s
    ));
  };

  const updateSignerPayRate = (userId: string, payRate: number) => {
    setSelectedSigners(selectedSigners.map(s =>
      s.userId === userId ? { ...s, payRate } : s
    ));
  };

  const updateSignerPayType = (userId: string, payType: 'hourly' | 'per_lead' | 'flat') => {
    setSelectedSigners(selectedSigners.map(s =>
      s.userId === userId ? { ...s, payType } : s
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userProfile) return;

    // For provider_admin, use the first signer's organizationId if they don't have one
    let effectiveOrgId = userProfile.organizationId;
    if (!effectiveOrgId && isProviderAdmin && selectedSigners.length > 0) {
      const firstSigner = users.find(u => u.uid === selectedSigners[0].userId);
      effectiveOrgId = firstSigner?.organizationId || 'provider';
    }

    if (!effectiveOrgId) {
      toast.error('Unable to determine organization. Please select at least one signer.');
      return;
    }

    setLoading(true);
    try {
      const signers = selectedSigners.map(signer => {
        const user = users.find(u => u.uid === signer.userId);
        return {
          userId: signer.userId,
          userName: user?.name || '',
          email: user?.email || '',
          role: user?.role || '',
          payRate: signer.payRate,
          payType: signer.payType,
          mandatory: signer.mandatory
        };
      });

      const contractId = await contractService.createContractFromTemplate(
        template.id,
        signers,
        effectiveOrgId,
        currentUser.uid,
        userProfile.name,
        projectId || undefined
      );

      // Create tasks for each signer
      const selectedProject = projects.find(p => p.id === projectId);
      for (const signer of signers) {
        await contractService.createContractSignatureTask({
          organizationId: effectiveOrgId,
          projectId: projectId || undefined,
          projectName: selectedProject?.name,
          contractId,
          contractTitle: template.name,
          assignedTo: signer.userId,
          assignedToName: signer.userName,
          assignedBy: currentUser.uid,
          assignedByName: userProfile.name
        });
      }

      toast.success(`Contract sent to ${signers.length} user${signers.length !== 1 ? 's' : ''}`);
      onSent();
    } catch (error) {
      console.error('Error sending contract:', error);
      toast.error('Failed to send contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Send Contract</h2>
              <p className="text-sm text-gray-500 mt-1">From template: {template.name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Template Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <FileText className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <div className="font-medium text-gray-800">{template.name}</div>
                <div className="text-sm text-gray-500">{template.documentName}</div>
              </div>
              {template.mandatory && (
                <span className="ml-auto px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                  Mandatory
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project (Optional)</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">No project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
            {loadingUsers ? (
              <div className="text-sm text-gray-500">Loading users...</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-xl p-2">
                {users.map((user) => {
                  const isSelected = selectedSigners.some(s => s.userId === user.uid);
                  const signer = selectedSigners.find(s => s.userId === user.uid);

                  return (
                    <div key={user.uid} className={`p-3 rounded-lg transition-colors ${isSelected ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50'}`}>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleUser(user.uid)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-700">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email} - {user.role}</div>
                        </div>
                      </label>

                      {/* Pay Rate Fields - shown when user is selected */}
                      {isSelected && signer && (
                        <div className="mt-3 pt-3 border-t border-primary-100 space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Pay Type</label>
                              <select
                                value={signer.payType}
                                onChange={(e) => updateSignerPayType(user.uid, e.target.value as any)}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              >
                                <option value="hourly">Hourly</option>
                                <option value="per_lead">Per Lead</option>
                                <option value="flat">Flat Rate</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                {signer.payType === 'hourly' ? 'Rate/Hour' : signer.payType === 'per_lead' ? 'Rate/Lead' : 'Total Amount'}
                              </label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={signer.payRate}
                                  onChange={(e) => updateSignerPayRate(user.uid, parseFloat(e.target.value) || 0)}
                                  className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                            </div>
                          </div>
                          {/* Mandatory Toggle */}
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={signer.mandatory}
                              onChange={(e) => updateSignerMandatory(user.uid, e.target.checked)}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-xs text-amber-700 font-medium">
                              Mandatory - Block app until signed
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || selectedSigners.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Contract
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Contract Modal Component
interface CreateContractModalProps {
  onClose: () => void;
  onCreated: () => void;
  projects: any[];
}

const CreateContractModal: React.FC<CreateContractModalProps> = ({ onClose, onCreated, projects }) => {
  const { currentUser, userProfile } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [selectedSigners, setSelectedSigners] = useState<{ userId: string; mandatory: boolean }[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [contractType, setContractType] = useState<'general' | 'w9' | 'direct_deposit' | 'employment' | 'nda' | 'other'>('general');

  const isProviderAdmin = userProfile?.role === 'provider_admin';
  const isProviderUser = userProfile?.role === 'provider_user';
  const isAdmin = userProfile?.role === 'provider_admin' || userProfile?.role === 'client_admin';

  useEffect(() => {
    loadUsers();
  }, [userProfile]);

  const loadUsers = async () => {
    if (!userProfile) {
      setLoadingUsers(false);
      return;
    }

    try {
      let userList: UserProfile[];

      // Provider_admin can see ALL users across all organizations
      if (isProviderAdmin) {
        userList = await userService.getAllUsers();
      } else if (userProfile.organizationId) {
        userList = await userService.getOrganizationUsers(userProfile.organizationId);
      } else {
        setLoadingUsers(false);
        return;
      }

      // Provider_user can only send contracts to field users
      if (isProviderUser) {
        userList = userList.filter(u => u.role === 'field');
      }
      setUsers(userList);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleSigner = (userId: string) => {
    const existing = selectedSigners.find(s => s.userId === userId);
    if (existing) {
      setSelectedSigners(selectedSigners.filter(s => s.userId !== userId));
    } else {
      setSelectedSigners([...selectedSigners, { userId, mandatory: false }]);
    }
  };

  const updateSignerMandatory = (userId: string, mandatory: boolean) => {
    setSelectedSigners(selectedSigners.map(s =>
      s.userId === userId ? { ...s, mandatory } : s
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userProfile || !file) return;

    // For provider_admin, use the first signer's organizationId if they don't have one
    let effectiveOrgId = userProfile.organizationId;
    if (!effectiveOrgId && isProviderAdmin && selectedSigners.length > 0) {
      const firstSigner = users.find(u => u.uid === selectedSigners[0].userId);
      effectiveOrgId = firstSigner?.organizationId || 'provider';
    }

    if (!effectiveOrgId) {
      toast.error('Unable to determine organization. Please select at least one signer.');
      return;
    }

    setLoading(true);
    try {
      const signers = selectedSigners.map(signer => {
        const user = users.find(u => u.uid === signer.userId);
        return {
          userId: signer.userId,
          userName: user?.name || '',
          email: user?.email || '',
          role: user?.role || '',
          mandatory: signer.mandatory
        };
      });

      const data: CreateContractDto = {
        title,
        description,
        documentFile: file,
        projectId: projectId || undefined,
        requiredSigners: signers,
        contractType
      };

      const contractId = await contractService.createContract(
        data,
        effectiveOrgId,
        currentUser.uid,
        userProfile.name
      );

      // Create tasks for each signer
      const selectedProject = projects.find(p => p.id === projectId);
      for (const signer of signers) {
        await contractService.createContractSignatureTask({
          organizationId: effectiveOrgId,
          projectId: projectId || undefined,
          projectName: selectedProject?.name,
          contractId,
          contractTitle: title,
          assignedTo: signer.userId,
          assignedToName: signer.userName,
          assignedBy: currentUser.uid,
          assignedByName: userProfile.name
        });
      }

      toast.success('Contract created and assigned to signers');
      onCreated();
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error('Failed to create contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">New Contract</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Employment Agreement"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Optional description..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contract Type</label>
              <select
                value={contractType}
                onChange={(e) => setContractType(e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="general">General</option>
                <option value="w9">W-9 Tax Form</option>
                <option value="direct_deposit">Direct Deposit</option>
                <option value="employment">Employment Agreement</option>
                <option value="nda">NDA</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (Optional)</label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">No project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document (PDF)</label>
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center">
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-5 w-5 text-primary-500" />
                  <span className="text-sm text-gray-700">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm text-gray-500">Click to upload PDF</span>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Required Signers</label>
            {loadingUsers ? (
              <div className="text-sm text-gray-500">Loading users...</div>
            ) : !isProviderAdmin && !userProfile?.organizationId ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                Your account is not associated with an organization. Please contact an administrator.
              </div>
            ) : users.length === 0 ? (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500">
                No users available. {isProviderUser ? 'There are no field users in your organization.' : 'Add users to your organization first.'}
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-xl p-2">
                {users.map((user) => {
                  const isSelected = selectedSigners.some(s => s.userId === user.uid);
                  const signer = selectedSigners.find(s => s.userId === user.uid);

                  return (
                    <div key={user.uid} className={`p-3 rounded-lg transition-colors ${isSelected ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50'}`}>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSigner(user.uid)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-700">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email} - {user.role}</div>
                        </div>
                      </label>

                      {/* Mandatory Toggle - shown when user is selected */}
                      {isSelected && signer && (
                        <div className="mt-2 pt-2 border-t border-primary-100">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={signer.mandatory}
                              onChange={(e) => updateSignerMandatory(user.uid, e.target.checked)}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-xs text-amber-700 font-medium">
                              Mandatory - Block app until signed
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !file || selectedSigners.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Contract
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Contract Detail Modal Component
interface ContractDetailModalProps {
  contract: Contract;
  onClose: () => void;
  onSigned: () => void;
}

const ContractDetailModal: React.FC<ContractDetailModalProps> = ({ contract, onClose, onSigned }) => {
  const { currentUser, userProfile } = useAuth();
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [showSignature, setShowSignature] = useState(false);

  const userSigner = contract.requiredSigners.find(s => s.userId === currentUser?.uid);
  const needsToSign = userSigner && !userSigner.signed;

  const handleSign = async () => {
    if (!currentUser || !userProfile || !signatureData) return;

    setSigning(true);
    try {
      await contractService.addSignature(contract.id, {
        fieldId: 'main',
        signedBy: currentUser.uid,
        signedByName: userProfile.name || 'Unknown',
        signedByEmail: userProfile.email || 'Unknown',
        signatureData,
        signatureType: 'drawn',
        ipAddress: 'not-captured',
        userAgent: navigator.userAgent || 'unknown',
        documentHash: 'not-captured',
        auditTrail: []
      });

      // Save signature profile
      await contractService.saveSignatureProfile(currentUser.uid, signatureData, 'drawn');

      // Complete the contract signature task
      await contractService.completeContractTask(contract.id, currentUser.uid);

      toast.success('Contract signed successfully');
      onSigned();
      onClose();
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error('Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{contract.title}</h2>
              {contract.description && (
                <p className="text-sm text-gray-500 mt-1">{contract.description}</p>
              )}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Document Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-800">Document</h3>
              <a
                href={contract.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </a>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
              <FileText className="h-8 w-8 text-primary-500" />
              <div>
                <div className="font-medium text-gray-800">{contract.documentName}</div>
                <div className="text-sm text-gray-500">
                  Created {formatSafeDate(contract.createdAt, 'MMM d, yyyy')}
                </div>
              </div>
            </div>
          </div>

          {/* Signers */}
          <div>
            <h3 className="font-medium text-gray-800 mb-3">Signers</h3>
            <div className="space-y-2">
              {contract.requiredSigners.map((signer) => (
                <div
                  key={signer.userId}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    signer.signed
                      ? 'bg-accent-50 border-accent-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                      signer.signed ? 'bg-accent-500' : 'bg-gray-400'
                    }`}>
                      {signer.userName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">{signer.userName}</div>
                      <div className="text-sm text-gray-500">{signer.email}</div>
                    </div>
                  </div>
                  {signer.signed ? (
                    <span className="flex items-center gap-1 text-sm text-accent-600">
                      <CheckCircle className="h-4 w-4" />
                      Signed
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="h-4 w-4" />
                      Pending
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Signature Section */}
          {needsToSign && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="font-medium text-gray-800 mb-3">Your Signature</h3>
              {!showSignature ? (
                <button
                  onClick={() => setShowSignature(true)}
                  className="w-full py-4 border-2 border-dashed border-primary-300 rounded-xl text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  <Pen className="h-5 w-5 mx-auto mb-1" />
                  <span>Click to add your signature</span>
                </button>
              ) : (
                <div className="space-y-4">
                  <SignatureCanvas
                    onSave={setSignatureData}
                    onClear={() => setSignatureData(null)}
                  />
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowSignature(false);
                        setSignatureData(null);
                      }}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSign}
                      disabled={!signatureData || signing}
                      className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-xl hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {signing ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          Signing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          Sign Contract
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Already Signed */}
          {userSigner?.signed && (
            <div className="bg-accent-50 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-accent-600" />
              <div>
                <div className="font-medium text-accent-800">You have signed this contract</div>
                <div className="text-sm text-accent-600">
                  Thank you for completing your signature.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractsPage;
